import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2, Loader2, MessageCircle, Briefcase, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import SiteLayout from "@/components/site/SiteLayout";
import { OFFICES, CONTACT } from "@/constants/siteData";
import { SID } from "@/constants/siteIds";
import { trackEvent, GA_EVENTS } from "@/lib/analytics";
import { getCmsApiBaseUrl } from "@/lib/cmsBase";

const API = getCmsApiBaseUrl();

const QUICK_LINKS = [
  { Icon: Briefcase, t: "Sales", d: "Talk to a CoSec specialist about plans and migration.", interest: "cosec-app" },
  { Icon: LifeBuoy, t: "Support", d: "Existing customer? Reach our support team 24/7.", interest: "support" },
  { Icon: MessageCircle, t: "Partnership", d: "Integrations, referrals and co-marketing.", interest: "partnership" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", interest: "cosec-app", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact-submissions`, {
        data: {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          company: form.company.trim(),
          interest: form.interest || "other",
          message: form.message.trim(),
          source: "website-contact",
        },
      });
      setDone(true);
      toast.success("Thanks, we'll be in touch shortly.");
      // GA4 conversion event (no PII, interest only)
      trackEvent(GA_EVENTS.CONTACT_SUBMIT, {
        form_id: "contact_main",
        interest: form.interest || "other",
        has_phone: !!form.phone,
        has_company: !!form.company,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not submit your message.";
      toast.error(typeof msg === "string" ? msg : "Could not submit your message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-24 pb-12 text-center max-w-3xl mx-auto">
          <span className="section-eyebrow">Contact us</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04]">
            We&apos;re here to{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-accent">help.</span>
              <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            Whether you have specific questions about our software, are looking at our technology, or exploring partnership opportunities, our team is ready to talk.
          </p>
        </div>
      </section>

      {/* Quick links */}
      <section className="pb-10">
        <div className="container-cv grid md:grid-cols-3 gap-5">
          {QUICK_LINKS.map(({ Icon, t, d, interest }, i) => (
            <button
              key={i}
              onClick={() => { setForm({ ...form, interest }); document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" }); }}
              className="text-left rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all"
            >
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-display text-lg font-bold text-primary">{t}</div>
              <div className="mt-1 text-sm text-muted-foreground">{d}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Form + offices */}
      <section className="py-12 md:py-20">
        <div className="container-cv grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7" id="contact-form">
            <div className="rounded-3xl border border-border bg-white p-6 md:p-10">
              {!done ? (
                <>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">Send us a message</h2>
                  <p className="mt-2 text-sm text-muted-foreground">We typically reply within 1 working day.</p>
                  <form onSubmit={submit} className="mt-6 grid gap-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-primary/70">Name *</Label>
                        <Input id="name" data-testid={SID.contactName} value={form.name} onChange={onChange("name")} placeholder="Your full name" className="mt-1.5 h-11" required />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-primary/70">Email *</Label>
                        <Input id="email" data-testid={SID.contactEmail} type="email" value={form.email} onChange={onChange("email")} placeholder="you@company.co.uk" className="mt-1.5 h-11" required />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-primary/70">Phone</Label>
                        <Input id="phone" data-testid={SID.contactPhone} value={form.phone} onChange={onChange("phone")} placeholder="+44 …" className="mt-1.5 h-11" />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-xs font-bold uppercase tracking-wider text-primary/70">Company</Label>
                        <Input id="company" data-testid={SID.contactCompany} value={form.company} onChange={onChange("company")} placeholder="Your firm or company" className="mt-1.5 h-11" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="interest" className="text-xs font-bold uppercase tracking-wider text-primary/70">I&apos;m interested in</Label>
                      <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                        <SelectTrigger data-testid={SID.contactInterest} className="mt-1.5 h-11">
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cosec-app">CoSec App (entity management)</SelectItem>
                          <SelectItem value="coverify">CoVerify (ID verification)</SelectItem>
                          <SelectItem value="partnership">Partnership / Integration</SelectItem>
                          <SelectItem value="support">Existing customer support</SelectItem>
                          <SelectItem value="other">Something else</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-primary/70">Message *</Label>
                      <Textarea id="message" data-testid={SID.contactMessage} value={form.message} onChange={onChange("message")} rows={5} placeholder="Tell us a bit about your team and what you're looking for." className="mt-1.5" required />
                    </div>
                    <Button
                      type="submit"
                      data-testid={SID.contactSubmit}
                      disabled={submitting}
                      className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-bold w-full sm:w-auto"
                    >
                      {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <>Send message <ArrowRight className="ml-2 h-4 w-4" /></>}
                    </Button>
                  </form>
                </>
              ) : (
                <div data-testid={SID.contactSuccess} className="text-center py-10">
                  <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-accent text-primary">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h2 className="mt-5 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">Message sent</h2>
                  <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-md mx-auto">
                    Thank you, our team will be in touch within 1 working day. For urgent matters, call {CONTACT.phone}.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Offices + contact */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl border border-border bg-primary text-primary-foreground p-6 md:p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
              <div className="relative">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Talk to us directly</div>
                <a href={CONTACT.phoneHref} className="mt-3 flex items-center gap-3 hover:text-accent transition-colors">
                  <Phone className="h-5 w-5 text-accent" />
                  <span className="font-display text-xl font-bold">{CONTACT.phone}</span>
                </a>
                <a href={CONTACT.emailHref} className="mt-2 flex items-center gap-3 hover:text-accent transition-colors">
                  <Mail className="h-5 w-5 text-accent" />
                  <span className="font-display text-base font-semibold">{CONTACT.email}</span>
                </a>
                <div className="mt-5 text-xs text-white/60">
                  Support is available Mon–Fri 09:00–18:00 UK time. Outside of those hours, leave a message and we&apos;ll get back to you on the next working day.
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-display text-lg font-bold text-primary">Visit us</h3>
              <div className="mt-4 space-y-3">
                {OFFICES.map((o, i) => (
                  <div key={i} className="rounded-2xl border border-border bg-white p-5 flex items-start gap-4">
                    <div className="h-10 w-10 grid place-items-center rounded-lg bg-accent/15 text-primary shrink-0">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-primary">{o.city}</div>
                      <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{o.address}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
